import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  getInspirationByProject,
} from "../../../../../services/inspirationsService";
import { getRoomStyles } from "../../../../../services/roomService";
import MoodboardImageModal from "./MoodboardImageModal";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending", bg: "#F3F4F6", color: "#374151" },
  {
    value: "SENT_TO_USER",
    label: "Sent to Customer",
    bg: "#DBEAFE",
    color: "#1E40AF",
  },
  { value: "APPROVED", label: "Approved", bg: "#D1FAE5", color: "#065F46" },
  { value: "REJECTED", label: "Rejected", bg: "#FEE2E2", color: "#991B1B" },
  {
    value: "REQUESTED_FOR_CHANGE",
    label: "Changes Requested",
    bg: "#FEF3C7",
    color: "#92400E",
  },
];

const SectionCard = ({ title, count, children }) => (
  <Box
    sx={{
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      backgroundColor: "#fff",
      p: 3,
      mb: 3,
    }}
  >
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0D0D0E" }}>
        {title}
        {typeof count === "number" && (
          <Box
            component="span"
            sx={{ color: "#6B7280", fontWeight: 400, ml: 0.5 }}
          >
            ({count})
          </Box>
        )}
      </Typography>
    </Box>
    {children}
  </Box>
);

const StatusPill = ({ status }) => {
  const cfg = STATUS_OPTIONS.find((o) => o.value === status);
  if (!cfg) return null;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 12,
      }}
    />
  );
};

const Inspirations = ({ projectId }) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [styles, setStyles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const items = board?.items || [];
  const currentSpace = items.filter((i) => i.category === "current_space");
  const moodboardImages = items.filter(
    (i) => i.category === "moodboard" && i.type === "image"
  );
  const moodboardLinks = items.filter(
    (i) => i.category === "moodboard" && i.type === "link"
  );
  const moodboardDocs = items.filter(
    (i) => i.category === "moodboard" && i.type === "document"
  );

  const selectedStyle = useMemo(
    () => styles.find((s) => s.id === board?.style_id) || null,
    [styles, board?.style_id]
  );

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [boardRes, stylesRes] = await Promise.all([
          getInspirationByProject(projectId).catch(() => null),
          getRoomStyles().catch(() => null),
        ]);
        if (cancelled) return;
        setBoard(boardRes?.success ? boardRes.data || null : null);
        setStyles(
          stylesRes?.success && Array.isArray(stylesRes.data)
            ? stylesRes.data
            : []
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!board) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          border: "1px dashed #D1D5DB",
          borderRadius: 2,
          color: "#6B7280",
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0D0D0E" }}>
          No inspiration board yet
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
          A board will appear here once one has been created.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#0D0D0E" }}>
          Inspirations
        </Typography>
        <StatusPill status={board.status} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
        }}
      >
        <SectionCard title="Brief">
          <Typography
            sx={{
              fontSize: 14,
              color: board.brief ? "#0D0D0E" : "#9CA3AF",
              whiteSpace: "pre-wrap",
            }}
          >
            {board.brief || "No brief added."}
          </Typography>
        </SectionCard>

        <SectionCard title="Preferred Style">
          {selectedStyle ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                component="img"
                src={selectedStyle.image_url}
                alt={selectedStyle.name}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
              <Typography
                sx={{ fontSize: 15, fontWeight: 500, fontStyle: "italic" }}
              >
                {selectedStyle.name}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 14, color: "#9CA3AF" }}>
              No preferred style selected.
            </Typography>
          )}
        </SectionCard>
      </Box>

      <SectionCard title="Current Space" count={currentSpace.length}>
        {currentSpace.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
            No current-space images.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
            }}
          >
            {currentSpace.map((img) => (
              <Box
                key={img.id}
                sx={{
                  borderRadius: 1.5,
                  overflow: "hidden",
                  aspectRatio: "1 / 1",
                  border: "1px solid #F3F4F6",
                }}
              >
                <Box
                  component="img"
                  src={img.image_thumbnail || img.url || img.path}
                  alt=""
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>

      <SectionCard title="Moodboard">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}
            >
              Images ({moodboardImages.length})
            </Typography>
            {moodboardImages.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No moodboard images.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                }}
              >
                {moodboardImages.map((img, i) => (
                  <Box
                    key={img.id}
                    onClick={() => {
                      setModalIndex(i);
                      setModalOpen(true);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      overflow: "hidden",
                      aspectRatio: "1 / 1",
                      border: "1px solid #F3F4F6",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "scale(1.02)" },
                    }}
                  >
                    <Box
                      component="img"
                      src={img.image_thumbnail || img.url || img.path}
                      alt=""
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}
            >
              Links ({moodboardLinks.length})
            </Typography>
            {moodboardLinks.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No links.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {moodboardLinks.map((link) => (
                  <Box
                    key={link.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      border: "1px solid #E5E7EB",
                      borderRadius: 1.5,
                      p: 1.5,
                    }}
                  >
                    <LinkIcon sx={{ color: "#6B7280" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#0D0D0E",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {link.title || link.url}
                      </Typography>
                      {link.title && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#6B7280",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {link.url}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}
            >
              Documents ({moodboardDocs.length})
            </Typography>
            {moodboardDocs.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No documents.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {moodboardDocs.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      border: "1px solid #E5E7EB",
                      borderRadius: 1.5,
                      p: 1.5,
                    }}
                  >
                    <DescriptionIcon sx={{ color: "#6B7280" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#0D0D0E",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {doc.title || doc.file_name || "Document"}
                      </Typography>
                      {doc.mime_type && (
                        <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                          {doc.mime_type}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      component="a"
                      href={doc.url || doc.path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </SectionCard>

      <MoodboardImageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        images={moodboardImages}
        initialIndex={modalIndex}
      />
    </Box>
  );
};

export default Inspirations;
